import { useState, useEffect, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, MoreHorizontal, FolderKanban, Pencil, Trash2, ArrowLeft, Copy, CheckCircle2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COVER_IMAGES = [
  "https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&q=80",
  "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80",
  "https://images.unsplash.com/photo-1558655146-d09347e0c766?auto=format&fit=crop&q=80",
  "https://images.unsplash.com/photo-1557683316-973673baf926?auto=format&fit=crop&q=80",
  "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80",
];

export default function ProjectsPage() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, token } = useAuth();
  const [client, setClient] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({ name: "", description: "", cover_image: COVER_IMAGES[0] });

  const fetchData = useCallback(async () => {
    try {
      const [clientRes, projectsRes] = await Promise.all([
        axios.get(`${API}/clients/${clientId}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/projects?client_id=${clientId}`, { headers: getAuthHeaders() })
      ]);
      setClient(clientRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error("Fetch error:", error);
      if (error.response?.status === 404) {
        navigate("/clients");
      }
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  }, [clientId, getAuthHeaders, navigate]);

  useEffect(() => {
    if (token) {
      fetchData();
    }
  }, [token, fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("Nome é obrigatório");
      return;
    }

    try {
      if (editingProject) {
        await axios.put(`${API}/projects/${editingProject.id}`, { ...formData, client_id: clientId }, { headers: getAuthHeaders() });
        toast.success("Projeto atualizado!");
      } else {
        await axios.post(`${API}/projects`, { ...formData, client_id: clientId }, { headers: getAuthHeaders() });
        toast.success("Projeto criado!");
      }
      setDialogOpen(false);
      setEditingProject(null);
      setFormData({ name: "", description: "", cover_image: COVER_IMAGES[0] });
      fetchData();
    } catch (error) {
      console.error("Save project error:", error);
      toast.error("Erro ao salvar projeto");
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name,
      description: project.description || "",
      cover_image: project.cover_image
    });
    setDialogOpen(true);
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm("Tem certeza que deseja excluir este projeto? Todas as tarefas serão removidas.")) {
      return;
    }
    try {
      await axios.delete(`${API}/projects/${projectId}`, { headers: getAuthHeaders() });
      toast.success("Projeto excluído!");
      fetchData();
    } catch (error) {
      console.error("Delete project error:", error);
      toast.error("Erro ao excluir projeto");
    }
  };

  const copyShareLink = (shareToken) => {
    const url = `${window.location.origin}/p/${shareToken}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copiado para a área de transferência!");
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="animate-pulse space-y-8">
          <div className="h-8 w-48 bg-muted rounded" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <Card key={i}>
                <div className="h-40 bg-muted rounded-t-lg" />
                <CardContent className="p-4">
                  <div className="h-6 w-32 bg-muted rounded" />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8" data-testid="projects-page">
      {/* Header */}
      <div className="flex flex-col gap-4">
        <Link to="/clients" className="flex items-center gap-2 text-muted-foreground hover:text-foreground w-fit">
          <ArrowLeft className="h-4 w-4" />
          <span>Voltar para Clientes</span>
        </Link>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div
              className="h-12 w-12 rounded-full flex items-center justify-center text-white font-bold text-xl"
              style={{ backgroundColor: client?.color }}
            >
              {client?.name?.[0]?.toUpperCase()}
            </div>
            <div>
              <h1 className="text-3xl font-bold heading-font">{client?.name}</h1>
              <p className="text-muted-foreground">Projetos do cliente</p>
            </div>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) {
              setEditingProject(null);
              setFormData({ name: "", description: "", cover_image: COVER_IMAGES[0] });
            }
          }}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-[#5AD4EB] to-[#00A7E1] text-[#004E8A] font-semibold hover:opacity-90" data-testid="new-project-btn">
                <Plus className="mr-2 h-4 w-4" />
                Novo Projeto
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <form onSubmit={handleSubmit}>
                <DialogHeader>
                  <DialogTitle className="heading-font">{editingProject ? "Editar Projeto" : "Novo Projeto"}</DialogTitle>
                  <DialogDescription>
                    {editingProject ? "Atualize as informações do projeto" : "Crie um novo projeto para este cliente"}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Nome do Projeto *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Ex: Website Institucional"
                      data-testid="project-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="description">Descrição</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Descreva o projeto..."
                      data-testid="project-description-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Capa do Projeto</Label>
                    <div className="grid grid-cols-5 gap-2">
                      {COVER_IMAGES.map((img, i) => (
                        <button
                          key={i}
                          type="button"
                          className={`aspect-video rounded-md bg-cover bg-center transition-all ${formData.cover_image === img ? "ring-2 ring-accent ring-offset-2" : "hover:opacity-80"}`}
                          style={{ backgroundImage: `url(${img})` }}
                          onClick={() => setFormData({ ...formData, cover_image: img })}
                          data-testid={`cover-image-${i}`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="submit" className="bg-gradient-to-r from-[#5AD4EB] to-[#00A7E1] text-[#004E8A] font-semibold hover:opacity-90" data-testid="save-project-btn">
                    {editingProject ? "Salvar" : "Criar Projeto"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Projects Grid */}
      {projects.length === 0 ? (
        <div className="text-center py-16">
          <FolderKanban className="h-16 w-16 mx-auto text-muted-foreground/50" />
          <p className="text-lg font-medium mt-4">Nenhum projeto ainda</p>
          <p className="text-muted-foreground mt-2">Crie o primeiro projeto para este cliente</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <Card key={project.id} className="group overflow-hidden hover:shadow-md transition-shadow" data-testid={`project-card-${project.id}`}>
              <div
                className="h-40 bg-cover bg-center relative"
                style={{ backgroundImage: `url(${project.cover_image})` }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute top-3 right-3">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="secondary" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`project-menu-${project.id}`}>
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleEdit(project)} data-testid={`edit-project-${project.id}`}>
                        <Pencil className="mr-2 h-4 w-4" />
                        Editar
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => copyShareLink(project.share_token)} data-testid={`share-project-${project.id}`}>
                        <Copy className="mr-2 h-4 w-4" />
                        Copiar Link
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <a href={`/p/${project.share_token}`} target="_blank" rel="noopener noreferrer" data-testid={`open-public-${project.id}`}>
                          <ExternalLink className="mr-2 h-4 w-4" />
                          Abrir Link Público
                        </a>
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleDelete(project.id)} className="text-destructive" data-testid={`delete-project-${project.id}`}>
                        <Trash2 className="mr-2 h-4 w-4" />
                        Excluir
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <div className="absolute bottom-3 left-3 right-3">
                  <h3 className="text-white font-semibold text-lg truncate">{project.name}</h3>
                </div>
              </div>
              <CardContent className="p-4">
                {project.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{project.description}</p>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CheckCircle2 className="h-4 w-4" />
                    <span>{project.task_count} tarefas</span>
                  </div>
                  <Link to={`/projects/${project.id}`}>
                    <Button size="sm" className="bg-gradient-to-r from-[#5AD4EB] to-[#00A7E1] text-[#004E8A] font-semibold hover:opacity-90" data-testid={`open-kanban-${project.id}`}>
                      Abrir
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
